import React, { useState, useEffect, useMemo, useRef, useCallback, useDeferredValue } from 'react';
import { motion, AnimatePresence, LayoutGroup, MotionConfig } from 'framer-motion';
import { Search, X, Copy, Check, Phone, Smartphone, Mail, RotateCcw } from 'lucide-react';
import './ExtensionsDirectoryView.css';
import axiosInstance from '../utils/axiosConfig';
import { normalizarTexto } from '../utils/texto';
import { NumeroRodante } from './Proyectos/utils/motion';

// Extensiones fijas que no dependen de la base de datos
const FIXED_EXTENSIONS = {
  "SALAS DE CONFERENCIAS": [
    { nombre: "Sala 1 CA1", cargo: "Sala", extension: "2201", sede: "CA1", isFixed: true },
    { nombre: "Sala 2 CA1", cargo: "Sala", extension: "2202", sede: "CA1", isFixed: true },
    { nombre: "Sala 3 CA1", cargo: "Sala", extension: "2203", sede: "CA1", isFixed: true },
    { nombre: "Sala 1 CDN", cargo: "Sala", extension: "1993", sede: "CDN", isFixed: true },
    { nombre: "Sala 2 CDN", cargo: "Sala", extension: "1995", sede: "CDN", isFixed: true }
  ],
  "RECEPCIÓN": [
    { nombre: "Pacheco Daniela", cargo: "Recepcionista", extension: "1900", sede: "CA1", isFixed: true },
    { nombre: "Vigilancia CDN", cargo: "Vigilancia", extension: "1990", sede: "CDN", isFixed: true },
    { nombre: "Supervisor de Seguridad CDN", cargo: "Vigilancia", extension: "1952", sede: "CDN", isFixed: true }
  ],
  "SEGURIDAD INDUSTRIAL Y PCP": [
    { nombre: "Romana", cargo: "Romana", extension: "1928", sede: "CDN", isFixed: true }
  ]
};

// Mapeo de nombres de departamentos para normalización
const DEPARTMENT_MAPPINGS = {
  'I.T.S': 'TI',
  'ITS': 'TI',
  'SUPPLY CHAIN': 'ALMACEN Y DESPACHO',
  'SUPPLY & CHAIN': 'ALMACEN Y DESPACHO',
  'ALMACÉN Y DESPACHO': 'ALMACEN Y DESPACHO'
};

// Departamentos que van primero; el resto, alfabetico.
const DEPARTMENT_PRIORITY = {
  'PRESIDENCIA': 1,
  'GERENCIA GENERAL': 2,
  'FINANZAS': 3
};

const getDepartmentPriority = (departamento) => {
  if (!departamento) return 999;
  return DEPARTMENT_PRIORITY[departamento.toUpperCase()] || 999;
};

// Dentro de un departamento, primero los cargos de mas jerarquia.
const getCargoWeight = (cargo) => {
  if (!cargo) return 999;
  const cargoUpper = cargo.toUpperCase();
  if (cargoUpper.includes('PRESIDENTE')) return 1;
  if (cargoUpper.includes('VICEPRESIDENTE')) return 2;
  if (cargoUpper.includes('GERENTE')) return 3;
  if (cargoUpper.includes('DIRECTOR')) return 4;
  if (cargoUpper.includes('JEFE')) return 5;
  if (cargoUpper.includes('LÍDER')) return 5;
  if (cargoUpper.includes('COORDINADOR')) return 6;
  if (cargoUpper.includes('SUPERVISOR')) return 7;
  if (cargoUpper.includes('ANALISTA')) return 8;
  if (cargoUpper.includes('ESPECIALISTA')) return 7.5;
  if (cargoUpper.includes('ASISTENTE')) return 9;
  return 999;
};

const normalizeDepartmentName = (departamento) => {
  if (!departamento) return 'Sin asignar';
  const normalizedName = departamento.toUpperCase().trim();
  return DEPARTMENT_MAPPINGS[normalizedName] || normalizedName;
};

// ---------- Vistas ----------

const VISTAS = [
  {
    key: 'extensiones',
    label: 'Extensiones',
    icon: Phone,
    buscar: 'Busca por nombre, número, cargo o departamento',
    campos: ['nombre', 'extension', 'cargo'],
  },
  {
    key: 'directorio',
    label: 'Directorio corporativo',
    icon: Smartphone,
    buscar: 'Busca por nombre, línea, cargo o sede',
    campos: ['nombre', 'linea', 'cargo', 'sede'],
  },
  {
    key: 'correos',
    label: 'Correos',
    icon: Mail,
    buscar: 'Busca por nombre, correo, cargo o sede',
    campos: ['nombre', 'correo', 'cargo', 'sede'],
  },
];

// Grupos de "Copiar correos de" (vista Correos).
const GRUPOS_CORREO = [
  { key: 'todos', label: 'Toda la empresa', filtro: () => true },
  { key: 'CDN', label: 'CDN', filtro: (e) => e.sede === 'CDN' },
  { key: 'CA1', label: 'CA1', filtro: (e) => e.sede === 'CA1' },
  { key: 'Caracas', label: 'CCS', filtro: (e) => e.sede === 'Caracas' },
  { key: 'comercial', label: 'Equipo comercial', filtro: (e) => (e.area || '').toUpperCase() === 'COMERCIAL' },
];

// Las salas se leen por sede: primero las de CA1, luego las de CDN.
const ORDEN_SEDE_SALAS = ['CA1', 'CDN'];
const compararSedeSala = (departamento, a, b) => (departamento === 'SALAS DE CONFERENCIAS'
  ? ORDEN_SEDE_SALAS.indexOf(a.sede) - ORDEN_SEDE_SALAS.indexOf(b.sede)
  : 0);

// Personas que coinciden con sede y busqueda, agrupadas por departamento y en
// el orden de siempre (prioridad de departamento, jerarquia de cargo).
const agrupar = (lista, vista, sede, consulta) => {
  const q = normalizarTexto(consulta).trim();
  const campos = VISTAS.find((v) => v.key === vista).campos;
  const porDepto = new Map();
  lista.forEach((p) => {
    if (sede !== 'todas' && p.sede !== sede) return;
    if (q && !campos.some((c) => normalizarTexto(p[c]).includes(q)) && !normalizarTexto(p.departamento).includes(q)) return;
    if (!porDepto.has(p.departamento)) porDepto.set(p.departamento, []);
    porDepto.get(p.departamento).push(p);
  });
  const desempate = vista === 'extensiones'
    ? (a, b) => String(a.extension).localeCompare(String(b.extension))
    : (a, b) => a.nombre.localeCompare(b.nombre);
  return [...porDepto.entries()]
    .map(([departamento, personas]) => ({
      departamento,
      personas: personas.sort((a, b) => (getCargoWeight(a.cargo) - getCargoWeight(b.cargo)) || compararSedeSala(departamento, a, b) || desempate(a, b)),
    }))
    .sort((a, b) => (getDepartmentPriority(a.departamento) - getDepartmentPriority(b.departamento))
      || a.departamento.localeCompare(b.departamento));
};

// ---------- Utilidades ----------

// Portapapeles con respaldo para HTTP (la API moderna solo existe en contexto seguro).
const copiarAlPortapapeles = (texto) => {
  if (navigator.clipboard && window.isSecureContext) return navigator.clipboard.writeText(texto);
  const area = document.createElement('textarea');
  area.value = texto;
  area.style.position = 'fixed';
  area.style.left = '-999999px';
  document.body.appendChild(area);
  area.focus();
  area.select();
  try {
    document.execCommand('copy');
    area.remove();
    return Promise.resolve();
  } catch (error) {
    area.remove();
    return Promise.reject(error);
  }
};

// Marca en el texto lo que coincide con la busqueda (sin acentos ni mayusculas).
const Resaltado = ({ texto, consulta }) => {
  const t = String(texto ?? '');
  const q = normalizarTexto(consulta).trim();
  if (!q) return t;
  const i = normalizarTexto(t).indexOf(q);
  if (i < 0) return t;
  return (
    <>
      {t.slice(0, i)}
      <mark className="bg-amber-100 text-inherit rounded-[3px] px-px">{t.slice(i, i + q.length)}</mark>
      {t.slice(i + q.length)}
    </>
  );
};

const RESORTE = { type: 'spring', stiffness: 420, damping: 34 };
const RESORTE_PASTILLA = { type: 'spring', stiffness: 520, damping: 40 };

// Cambio de vista: el contenido entra y sale hacia el lado de la pestaña elegida.
const VARIANTES_VISTA = {
  entra: (dir) => ({ opacity: 0, x: dir * 24 }),
  quieta: { opacity: 1, x: 0 },
  sale: (dir) => ({ opacity: 0, x: dir * -24, transition: { duration: 0.14 } }),
};

// ---------- Controles ----------

// Selector con una pastilla que se desliza a la opcion elegida.
const Segmentado = ({ opciones, valor, onChange, grupo, oscuro = false }) => (
  <div className={`inline-flex flex-wrap p-1 rounded-xl ${oscuro ? 'bg-slate-100' : 'bg-slate-100/70'}`}>
    {opciones.map((o) => {
      const activo = o.key === valor;
      const Icono = o.icon;
      return (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={`relative px-3 py-1.5 text-sm rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sura-blue/40 ${
            activo
              ? (oscuro ? 'text-white' : 'text-sura-blue')
              : 'text-slate-500 hover:text-slate-900'
          }`}
        >
          {activo && (
            <motion.span
              layoutId={grupo}
              transition={RESORTE_PASTILLA}
              className={`absolute inset-0 rounded-lg ${oscuro ? 'bg-sura-blue shadow-sm shadow-sura-blue/20' : 'bg-white shadow-sm ring-1 ring-slate-200/70'}`}
            />
          )}
          <span className={`relative inline-flex items-center gap-1.5 ${activo ? 'font-medium' : ''}`}>
            {Icono && <Icono size={14} strokeWidth={2} />}
            {o.label}
          </span>
        </button>
      );
    })}
  </div>
);

// Boton que copia y lo confirma en su propio lugar: el texto sube y aparece
// "Copiado" con el check; a los dos segundos vuelve.
const BotonCopiar = ({ obtenerTexto, children, className = '', compacto = false, titulo }) => {
  const [estado, setEstado] = useState('listo');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const copiar = (e) => {
    e.preventDefault();
    e.stopPropagation();
    copiarAlPortapapeles(obtenerTexto())
      .then(() => setEstado('copiado'))
      .catch(() => setEstado('error'))
      .finally(() => {
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setEstado('listo'), 1800);
      });
  };

  const contenido = {
    listo: children,
    copiado: (
      <span className="inline-flex items-center gap-1.5">
        <Check size={compacto ? 13 : 14} strokeWidth={2.5} />
        {!compacto && 'Copiado'}
      </span>
    ),
    error: <span>{compacto ? '!' : 'No se pudo copiar'}</span>,
  }[estado];

  return (
    <motion.button
      layout
      type="button"
      onClick={copiar}
      title={titulo}
      transition={RESORTE}
      className={`relative inline-flex items-center justify-center overflow-hidden transition-colors ${
        estado === 'copiado' ? '!bg-emerald-600 !border-emerald-600 !text-white' : ''
      } ${estado === 'error' ? '!bg-rose-50 !border-rose-200 !text-rose-700' : ''} ${className}`}
    >
      <AnimatePresence mode="popLayout" initial={false}>
        <motion.span
          key={estado}
          initial={{ y: 12, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -12, opacity: 0 }}
          transition={RESORTE}
          className="inline-flex items-center"
        >
          {contenido}
        </motion.span>
      </AnimatePresence>
    </motion.button>
  );
};

// ---------- Tarjetas ----------

const TarjetaExtension = ({ p, consulta, conSede }) => (
  <div className="h-full flex items-center gap-3 rounded-xl border border-slate-200/80 bg-white px-3 py-2.5 transition-colors hover:border-sura-blue/30">
    <span className="shrink-0 min-w-[64px] rounded-lg bg-sura-blue/[0.06] px-2 py-2 text-center text-[19px] font-semibold leading-none tracking-tight text-sura-blue tabular-nums">
      <Resaltado texto={p.extension} consulta={consulta} />
    </span>
    <div className="min-w-0 flex-1">
      <p className="truncate text-sm font-medium text-slate-800"><Resaltado texto={p.nombre} consulta={consulta} /></p>
      <p className="text-xs leading-snug text-slate-500"><Resaltado texto={p.cargo} consulta={consulta} /></p>
    </div>
    {conSede && <span className="shrink-0 self-start text-[10px] font-medium text-slate-400">{p.sede}</span>}
  </div>
);

const TarjetaLinea = ({ p, consulta }) => (
  <div className="h-full flex flex-col gap-2 rounded-xl border border-slate-200/80 bg-white px-3.5 py-3 transition-colors hover:border-sura-blue/30">
    <a
      href={`tel:${p.linea}`}
      className="self-start text-[17px] font-semibold leading-none tracking-tight text-sura-blue tabular-nums hover:underline underline-offset-4"
    >
      <Resaltado texto={p.linea} consulta={consulta} />
    </a>
    <div className="min-w-0">
      <p className="truncate text-sm font-medium text-slate-800"><Resaltado texto={p.nombre} consulta={consulta} /></p>
      <p className="text-xs leading-snug text-slate-500"><Resaltado texto={p.cargo} consulta={consulta} /></p>
    </div>
    <span className="mt-auto text-[10px] font-medium text-slate-400">{p.sede}</span>
  </div>
);

const FilaCorreo = ({ p, consulta }) => {
  // Los correos @suraven son de la marca anterior: se ven, pero en segundo plano.
  const suraven = p.correo.toLowerCase().includes('@suraven.com');
  return (
    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1.2fr)_minmax(0,1.6fr)_auto] items-center gap-x-4 gap-y-1 px-4 py-3 bg-white">
      <div className="min-w-0">
        <p className="truncate text-sm font-medium text-slate-800"><Resaltado texto={p.nombre} consulta={consulta} /></p>
        <p className="text-xs leading-snug text-slate-500"><Resaltado texto={p.cargo} consulta={consulta} /></p>
      </div>
      <div className="flex min-w-0 items-center gap-2">
        <a
          href={`mailto:${p.correo}`}
          className={`truncate text-sm font-medium underline-offset-4 hover:underline ${suraven ? 'text-slate-500' : 'text-sura-blue'}`}
        >
          <Resaltado texto={p.correo} consulta={consulta} />
        </a>
        <BotonCopiar
          obtenerTexto={() => p.correo}
          compacto
          titulo="Copiar correo"
          className="h-7 w-7 shrink-0 rounded-lg border border-slate-200 bg-white text-slate-400 hover:border-sura-blue/40 hover:text-sura-blue"
        >
          <Copy size={13} strokeWidth={2} />
        </BotonCopiar>
      </div>
      <span className="justify-self-start sm:justify-self-end rounded-md bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-500">
        {p.sede}
      </span>
    </div>
  );
};

// ---------- Vista ----------

function ExtensionsDirectoryView() {
  const [datos, setDatos] = useState({ extensiones: [], directorio: [], correos: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [vista, setVista] = useState('extensiones');
  const [sede, setSede] = useState('todas');
  const [busqueda, setBusqueda] = useState('');
  // La lista se filtra con el valor diferido: escribir nunca se traba aunque
  // haya cientos de tarjetas reacomodandose.
  const consulta = useDeferredValue(busqueda);
  const buscador = useRef(null);
  // Direccion del cambio de vista (para que el contenido entre desde ese lado).
  const direccion = useRef(1);
  // Los departamentos entran en cascada solo al abrir una vista; despues, al
  // buscar o filtrar, aparecen sin demora.
  const [cascada, setCascada] = useState(true);
  useEffect(() => {
    setCascada(true);
    const t = setTimeout(() => setCascada(false), 700);
    return () => clearTimeout(t);
  }, [vista, loading]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: empleados } = await axiosInstance.get('/api/empleados');

      const extensiones = empleados
        .filter((emp) => emp.extension)
        .map((emp) => ({
          id: emp.id,
          sede: emp.sede || 'Sin asignar',
          nombre: emp.nombre || 'Sin asignar',
          departamento: normalizeDepartmentName(emp.departamento),
          cargo: emp.cargo || 'Sin asignar',
          extension: String(emp.extension),
        }));
      Object.entries(FIXED_EXTENSIONS).forEach(([departamento, fijas]) => {
        fijas.forEach((f) => extensiones.push({
          ...f,
          id: `fija-${f.extension}`,
          departamento: normalizeDepartmentName(departamento),
        }));
      });

      const directorio = empleados
        .filter((emp) => emp.linea)
        .map((emp) => ({
          id: emp.id,
          sede: emp.sede || 'Sin asignar',
          nombre: emp.nombre || 'Sin asignar',
          departamento: normalizeDepartmentName(emp.departamento),
          cargo: emp.cargo || 'Sin asignar',
          linea: emp.linea,
        }));

      const correos = empleados
        .filter((emp) => emp.correo && emp.correo.trim() !== '')
        .map((emp) => ({
          id: emp.id,
          sede: emp.sede || 'Sin asignar',
          nombre: emp.nombre || 'Sin asignar',
          departamento: normalizeDepartmentName(emp.departamento),
          area: emp.area || 'Sin asignar',
          cargo: emp.cargo || 'Sin asignar',
          correo: emp.correo.trim(),
        }));

      setDatos({ extensiones, directorio, correos });
      setError(null);
    } catch (err) {
      console.error('Error cargando el directorio:', err);
      setError('No se pudo cargar el directorio.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // "/" lleva al buscador; Esc lo limpia.
  useEffect(() => {
    const alTeclear = (e) => {
      const escribiendo = ['INPUT', 'TEXTAREA', 'SELECT'].includes(document.activeElement?.tagName)
        || document.activeElement?.isContentEditable;
      if (e.key === '/' && !escribiendo) {
        e.preventDefault();
        buscador.current?.focus();
      } else if (e.key === 'Escape' && document.activeElement === buscador.current) {
        setBusqueda('');
      }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, []);

  const lista = datos[vista];
  const grupos = useMemo(() => agrupar(lista, vista, sede, consulta), [lista, vista, sede, consulta]);
  const total = useMemo(() => grupos.reduce((s, g) => s + g.personas.length, 0), [grupos]);
  const sedes = useMemo(
    () => [...new Set(lista.map((p) => p.sede))].filter(Boolean).sort(),
    [lista]
  );
  const vistaActual = VISTAS.find((v) => v.key === vista);

  const cambiarVista = (nueva) => {
    if (nueva === vista) return;
    const orden = VISTAS.map((v) => v.key);
    direccion.current = orden.indexOf(nueva) > orden.indexOf(vista) ? 1 : -1;
    setVista(nueva);
    setSede('todas');
  };

  const correosDe = (filtro) => datos.correos.filter(filtro).map((e) => e.correo).join(', ');

  if (error) {
    return (
      <div className="max-w-[1400px] mx-auto pt-4 pb-12">
        <h2 className="page-title mb-6">Directorio</h2>
        <div className="flex flex-col items-center gap-3 py-16 text-center">
          <p className="text-slate-700 font-medium">{error}</p>
          <button
            type="button"
            onClick={fetchData}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
          >
            <RotateCcw size={14} /> Intentar de nuevo
          </button>
        </div>
      </div>
    );
  }

  return (
    <MotionConfig reducedMotion="user">
      <div className="extensions-directory max-w-[1400px] mx-auto pt-4 pb-12">
        <div className="mb-4">
          <h2 className="page-title">Directorio</h2>
          <p className="page-subtitle mt-1">Extensiones, líneas corporativas y correos de toda la empresa.</p>
        </div>

        {/* Controles: quedan a mano al bajar por la lista. */}
        <div className="no-print sticky top-0 z-20 -mx-2 px-2 pt-1 pb-3 bg-gradient-to-b from-[#F8FAFC] via-[#F8FAFC]/95 to-[#F8FAFC]/0">
          <div className="rounded-2xl border border-slate-200/80 bg-white p-2 shadow-sm">
            <div className="relative">
              <Search size={17} className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                ref={buscador}
                type="text"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
                placeholder={vistaActual.buscar}
                aria-label="Buscar en el directorio"
                className="h-11 w-full rounded-xl border border-transparent bg-slate-50 pl-10 pr-40 text-[15px] text-slate-800 placeholder:text-slate-400 outline-none transition-all focus:border-sura-blue/30 focus:bg-white focus:ring-4 focus:ring-sura-blue/10"
              />
              <div className="absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-2">
                <span className="text-xs text-slate-500 tabular-nums">
                  {loading ? '' : <><NumeroRodante valor={total} /> {total === 1 ? 'resultado' : 'resultados'}</>}
                </span>
                {busqueda ? (
                  <button
                    type="button"
                    onClick={() => { setBusqueda(''); buscador.current?.focus(); }}
                    aria-label="Limpiar búsqueda"
                    className="rounded-md p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <X size={15} />
                  </button>
                ) : (
                  <kbd className="hidden sm:inline-flex h-6 min-w-[24px] items-center justify-center rounded-md border border-slate-200 bg-white px-1.5 text-[11px] font-medium text-slate-400">/</kbd>
                )}
              </div>
            </div>

            <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
              <Segmentado opciones={VISTAS} valor={vista} onChange={cambiarVista} grupo="directorio-vista" oscuro />
              <Segmentado
                opciones={[{ key: 'todas', label: 'Todas' }, ...sedes.map((s) => ({ key: s, label: s }))]}
                valor={sede}
                onChange={setSede}
                grupo="directorio-sede"
              />
            </div>
          </div>
        </div>

        {loading ? (
          <Esqueleto />
        ) : (
          <AnimatePresence mode="wait" custom={direccion.current} initial={false}>
            <motion.div
              key={vista}
              custom={direccion.current}
              variants={VARIANTES_VISTA}
              initial="entra"
              animate="quieta"
              exit="sale"
              transition={RESORTE}
            >
              {vista === 'correos' && (
                <div className="no-print mb-5 flex flex-wrap items-center gap-2">
                  <span className="mr-1 text-sm text-slate-500">Copiar correos de</span>
                  {GRUPOS_CORREO.map((g) => {
                    const cantidad = datos.correos.filter(g.filtro).length;
                    if (cantidad === 0) return null;
                    return (
                      <BotonCopiar
                        key={g.key}
                        obtenerTexto={() => correosDe(g.filtro)}
                        titulo={`Copiar los ${cantidad} correos, separados por coma`}
                        className={`h-9 rounded-xl border px-3.5 text-sm font-medium ${
                          g.key === 'todos'
                            ? 'border-sura-blue bg-sura-blue text-white hover:bg-sura-blue-dark'
                            : 'border-slate-200 bg-white text-slate-700 hover:border-sura-blue/40 hover:text-sura-blue'
                        }`}
                      >
                        <span className="inline-flex items-center gap-2">
                          {g.label}
                          <span className={`tabular-nums text-xs ${g.key === 'todos' ? 'text-white/70' : 'text-slate-400'}`}>{cantidad}</span>
                        </span>
                      </BotonCopiar>
                    );
                  })}
                </div>
              )}

              {grupos.length === 0 ? (
                <SinResultados
                  busqueda={busqueda}
                  onLimpiar={() => { setBusqueda(''); setSede('todas'); buscador.current?.focus(); }}
                />
              ) : (
                <LayoutGroup id={`directorio-${vista}`}>
                  <div className="flex flex-col gap-11">
                    <AnimatePresence mode="popLayout">
                      {grupos.map((g, gi) => (
                        <Seccion
                          key={g.departamento}
                          grupo={g}
                          indice={gi}
                          cascada={cascada}
                          vista={vista}
                          consulta={consulta}
                          conSede={sede === 'todas'}
                          correos={vista === 'correos' ? g.personas.map((p) => p.correo).join(', ') : null}
                        />
                      ))}
                    </AnimatePresence>
                  </div>
                </LayoutGroup>
              )}
            </motion.div>
          </AnimatePresence>
        )}
      </div>
    </MotionConfig>
  );
}

// Un departamento: su nombre, cuantos hay y sus tarjetas (o filas de correo).
// Al filtrar, las tarjetas que quedan se deslizan a su nuevo lugar y las que
// salen se encogen; el departamento entero se va si se queda vacio.
const Seccion = React.forwardRef(({ grupo, indice, cascada, vista, consulta, conSede, correos }, ref) => (
  <motion.section
    ref={ref}
    layout="position"
    initial={{ opacity: 0, y: 14 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, transition: { duration: 0.15 } }}
    transition={{ ...RESORTE, delay: cascada ? Math.min(indice * 0.035, 0.28) : 0 }}
    className="directorio-seccion"
  >
    {/* Barra azul con el nombre del departamento */}
    <header className="mb-3 flex items-center gap-3 rounded-lg bg-sura-blue px-4 py-2">
      <h3 className="flex-1 text-sm font-semibold tracking-wide text-white">{grupo.departamento}</h3>
      {correos && (
        <BotonCopiar
          obtenerTexto={() => correos}
          titulo={`Copiar los correos de ${grupo.departamento}`}
          className="no-print h-7 rounded-lg border border-white/25 bg-white/10 px-2.5 text-xs font-medium text-white hover:bg-white/20"
        >
          <span className="inline-flex items-center gap-1.5"><Copy size={12} /> Copiar correos</span>
        </BotonCopiar>
      )}
    </header>

    {vista === 'correos' ? (
      <div className="overflow-hidden rounded-xl border border-slate-200/80 divide-y divide-slate-100">
        <AnimatePresence mode="popLayout" initial={false}>
          {grupo.personas.map((p) => (
            <motion.div
              key={p.id}
              layout="position"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={RESORTE}
            >
              <FilaCorreo p={p} consulta={consulta} />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    ) : (
      <div
        className="grid gap-2"
        style={{ gridTemplateColumns: `repeat(auto-fill, minmax(${vista === 'directorio' ? 230 : 250}px, 1fr))` }}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          {grupo.personas.map((p) => (
            <motion.div
              key={p.id}
              layout="position"
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={RESORTE}
            >
              {vista === 'directorio'
                ? <TarjetaLinea p={p} consulta={consulta} />
                : <TarjetaExtension p={p} consulta={consulta} conSede={conSede} />}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    )}
  </motion.section>
));

const SinResultados = ({ busqueda, onLimpiar }) => (
  <motion.div
    initial={{ opacity: 0, y: 8 }}
    animate={{ opacity: 1, y: 0 }}
    transition={RESORTE}
    className="flex flex-col items-center py-16 text-center"
  >
    <p className="font-medium text-slate-700">
      {busqueda ? `Nadie coincide con "${busqueda}"` : 'No hay nadie registrado en esta sede'}
    </p>
    <p className="mt-1 text-sm text-slate-500">Prueba con el nombre, el cargo o el número, o mira todas las sedes.</p>
    <button
      type="button"
      onClick={onLimpiar}
      className="mt-4 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
    >
      Ver todo el directorio
    </button>
  </motion.div>
);

const Esqueleto = () => (
  <div className="flex flex-col gap-11" aria-label="Cargando directorio">
    {[6, 4, 8].map((n, s) => (
      <div key={s}>
        <div className="mb-3 h-9 rounded-lg bg-sura-blue/80" />
        <div className="grid gap-2" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))' }}>
          {[...Array(n)].map((_, i) => (
            <div key={i} className="h-[58px] rounded-xl border border-slate-100 bg-white skeleton" />
          ))}
        </div>
      </div>
    ))}
  </div>
);

export default ExtensionsDirectoryView;
